import { useEffect, useMemo, useState } from 'react'

type WelcomeMode = 'chat' | 'agent'
type Language = 'no' | 'en'

interface WelcomeEntry {
  title: string
  subtitle: string
  input: string
}

interface CopyContext {
  weekday: string
  morningWeekday: string
  eveningWeekday: string
}

function getWeekdayNames(language: Language, date: Date): CopyContext {
  const locale = language === 'en' ? 'en-US' : 'nb-NO'
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date).toLowerCase()
  return {
    weekday,
    morningWeekday: weekday,
    eveningWeekday: weekday,
  }
}

function buildNoChatEntries(ctx: CopyContext): WelcomeEntry[] {
  return [
    {
      title: `Hva tenker du på denne ${ctx.weekday}natten?`,
      subtitle: 'Vi kan rydde tankene, skrive noe klart eller finne et godt svar mens det er stille.',
      input: 'Skriv det du vil tenke høyt om...',
    },
    {
      title: `Våken sent denne ${ctx.eveningWeekday}kvelden?`,
      subtitle: 'Jeg kan hjelpe deg med å lande en idé, et utkast eller en plan før du logger av.',
      input: 'Fortell meg hva du vil få på plass før natten...',
    },
    {
      title: `Tidlig ute denne ${ctx.morningWeekday}morgenen?`,
      subtitle: 'Vi kan starte rolig med en plan, et utkast eller et raskt overblikk.',
      input: 'Hva vil du komme godt i gang med i dag?',
    },
    {
      title: 'Hva vil du få klart før formiddagen setter fart?',
      subtitle: 'Jeg kan hjelpe deg med å skrive, strukturere og finne neste steg.',
      input: 'Beskriv det du vil ha hjelp til akkurat nå...',
    },
    {
      title: 'Hva trenger du et skarpt svar på i dag?',
      subtitle: 'Vi kan gå rett på analysen, eller ta det steg for steg hvis du vil.',
      input: 'Still spørsmålet ditt her...',
    },
    {
      title: 'Hva jobber du med akkurat nå?',
      subtitle: 'Jeg kan hjelpe deg med tekst, idéarbeid, research eller problemløsing.',
      input: 'Skriv oppgaven, idéen eller spørsmålet ditt...',
    },
    {
      title: 'Hva vil du bruke denne ettermiddagen på?',
      subtitle: 'La oss få noe konkret ut døren: en plan, et utkast eller en beslutning.',
      input: 'Hva vil du komme videre på nå?',
    },
    {
      title: 'Hva trenger du å få oversikt over?',
      subtitle: 'Jeg kan destillere kaos til noe tydelig, kort og brukbart.',
      input: 'Lim inn det du vil ha sortert eller forklart...',
    },
    {
      title: 'Hva vil du få landet før dagen glir videre?',
      subtitle: 'Vi kan skrive, tenke, sammenligne eller bare rydde opp i det som er uklart.',
      input: 'Fortell meg hva som må falle på plass...',
    },
    {
      title: `Hva vil du få unna denne ${ctx.eveningWeekday}kvelden?`,
      subtitle: 'Jeg kan hjelpe deg med å avslutte dagen med et svar, et utkast eller en plan.',
      input: 'Hva vil du bli ferdig med før kvelden er over?',
    },
    {
      title: 'Hva vil du sende, skrive eller forstå i kveld?',
      subtitle: 'Dette er et fint tidspunkt for å få ro på ting som har hengt litt i lufta.',
      input: 'Skriv det du vil ha hjelp til i kveld...',
    },
    {
      title: 'Hva trenger du hjelp med akkurat nå?',
      subtitle: 'Jeg kan være sparringspartner, skriver eller problemløser, alt etter hva som trengs.',
      input: 'Start der du er, så tar vi det derfra...',
    },
  ]
}

function buildNoAgentEntries(ctx: CopyContext): WelcomeEntry[] {
  return [
    {
      title: `Hva skal agenten ta seg av denne ${ctx.weekday}natten?`,
      subtitle: 'Gi en oppgave, så kan agenten søke, analysere, skrive eller levere mens du følger med.',
      input: 'Beskriv oppgaven du vil at agenten skal løse...',
    },
    {
      title: `La agenten jobbe for deg denne ${ctx.eveningWeekday}kvelden`,
      subtitle: 'Den kan hente informasjon, lage en rapport eller fullføre et konkret oppdrag.',
      input: 'Fortell agenten hva den skal gjøre i kveld...',
    },
    {
      title: `Hva skal agenten få i gang denne ${ctx.morningWeekday}morgenen?`,
      subtitle: 'Et godt morgenoppdrag kan være research, rapport, analyse eller strukturering.',
      input: 'Gi agenten en oppgave å starte dagen med...',
    },
    {
      title: 'Hvilken oppgave vil du delegere først i dag?',
      subtitle: 'Agenten kan tenke selvstendig, bruke verktøy og levere et konkret resultat.',
      input: 'Beskriv oppgaven, ønsket resultat og eventuelle rammer...',
    },
    {
      title: 'Hva vil du at agenten skal undersøke for deg?',
      subtitle: 'Den kan søke på nett, hente kilder og komme tilbake med et bearbeidet svar.',
      input: 'Skriv hva agenten skal researche eller finne ut av...',
    },
    {
      title: 'Hva vil du at agenten skal levere?',
      subtitle: 'Rapport, oversikt, utkast, sammendrag eller analyse, den kan ta hele løpet.',
      input: 'Fortell hva du vil ha tilbake når oppgaven er ferdig...',
    },
    {
      title: 'Hva kan agenten ta av tallerkenen din nå?',
      subtitle: 'Gi den noe konkret og målrettet, så holder den tempoet oppe for deg.',
      input: 'Beskriv oppgaven du vil slippe å gjøre manuelt...',
    },
    {
      title: 'Hva trenger en grundig agentgjennomgang?',
      subtitle: 'Agenten kan jobbe i flere steg og holde tråden gjennom hele oppgaven.',
      input: 'Skriv oppgaven som om du briefet en god kollega...',
    },
    {
      title: 'Hvilken jobb vil du sette i gang før ettermiddagen går?',
      subtitle: 'Bra for research, oppsummeringer, filarbeid og leveranser som krever flere steg.',
      input: 'Gi agenten en konkret jobb å jobbe med nå...',
    },
    {
      title: `Hva skal agenten fullføre denne ${ctx.eveningWeekday}kvelden?`,
      subtitle: 'Dette er et fint tidspunkt for å få lukket en analyse, rapport eller praktisk oppgave.',
      input: 'Skriv hva agenten skal få ferdig før kvelden er over...',
    },
    {
      title: 'Hva vil du at agenten skal ta helt i mål?',
      subtitle: 'Den kan jobbe mer selvstendig når du beskriver mål, format og hva som er viktig.',
      input: 'Beskriv målet, leveransen og eventuelle begrensninger...',
    },
    {
      title: 'Hva skal agenten gå løs på akkurat nå?',
      subtitle: 'Den kan søke, skrive, analysere og levere, uten at du trenger å styre hvert steg.',
      input: 'Gi agenten oppgaven din her...',
    },
  ]
}

function buildEnChatEntries(ctx: CopyContext): WelcomeEntry[] {
  return [
    { title: `What are you thinking about this ${ctx.weekday} night?`, subtitle: 'We can sort through ideas, write something clear, or find a solid answer while it is quiet.', input: 'Write what is on your mind...' },
    { title: `Up late this ${ctx.eveningWeekday} evening?`, subtitle: 'I can help you land an idea, a draft, or a plan before you log off.', input: 'Tell me what you want to wrap up tonight...' },
    { title: `Starting early this ${ctx.morningWeekday} morning?`, subtitle: 'We can ease into the day with a plan, a draft, or a quick overview.', input: 'What do you want to get moving this morning?' },
    { title: 'What would you like to get done before the day speeds up?', subtitle: 'I can help you write, structure, and find the next step.', input: 'Describe what you want help with right now...' },
    { title: 'What do you need a sharp answer to today?', subtitle: 'We can go straight into analysis, or take it step by step.', input: 'Ask your question here...' },
    { title: 'What are you working on right now?', subtitle: 'I can help with writing, ideation, research, or problem solving.', input: 'Share the task, idea, or question...' },
    { title: 'What do you want to use this afternoon for?', subtitle: 'Let us get something concrete out the door: a plan, a draft, or a decision.', input: 'What do you want to move forward today?' },
    { title: 'What do you need a clearer overview of?', subtitle: 'I can turn chaos into something focused, short, and usable.', input: 'Paste in what you want sorted or explained...' },
    { title: 'What would you like to land before the day drifts on?', subtitle: 'We can write, think, compare, or simply clean up what feels unclear.', input: 'Tell me what needs to click into place...' },
    { title: `What would you like to finish this ${ctx.eveningWeekday} evening?`, subtitle: 'I can help you close the day with an answer, a draft, or a plan.', input: 'What do you want to finish before tonight ends?' },
    { title: 'What do you want to send, write, or understand tonight?', subtitle: 'This is a good time to settle things that have been hanging in the air.', input: 'Write what you want help with tonight...' },
    { title: 'What can I help you with right now?', subtitle: 'I can be a thinking partner, a writer, or a problem solver, depending on what you need.', input: 'Start where you are, and we will take it from there...' },
  ]
}

function buildEnAgentEntries(ctx: CopyContext): WelcomeEntry[] {
  return [
    { title: `What should the agent take on this ${ctx.weekday} night?`, subtitle: 'Give it a task and it can research, analyze, write, or deliver while you keep an eye on it.', input: 'Describe the task you want the agent to solve...' },
    { title: `Let the agent work for you this ${ctx.eveningWeekday} evening`, subtitle: 'It can gather information, produce a report, or finish a concrete assignment.', input: 'Tell the agent what to do tonight...' },
    { title: `What should the agent kick off this ${ctx.morningWeekday} morning?`, subtitle: 'A strong morning task could be research, a report, an analysis, or structured prep.', input: 'Give the agent a task to start the day with...' },
    { title: 'Which task do you want to delegate first today?', subtitle: 'The agent can think independently, use tools, and bring back a concrete result.', input: 'Describe the task, the expected result, and any constraints...' },
    { title: 'What do you want the agent to investigate for you?', subtitle: 'It can search the web, gather sources, and come back with a processed answer.', input: 'Write what the agent should research...' },
    { title: 'What do you want the agent to deliver?', subtitle: 'Report, overview, draft, summary, or analysis, it can take the full run.', input: 'Tell the agent what you want back when it is done...' },
    { title: 'What can the agent take off your plate right now?', subtitle: 'Give it something concrete and goal-oriented, and let it carry the momentum.', input: 'Describe the task you want to stop doing manually...' },
    { title: 'What needs a thorough agent pass?', subtitle: 'The agent can work in several steps and keep the thread through the whole job.', input: 'Write the task like you were briefing a strong teammate...' },
    { title: 'Which job should the agent tackle before the afternoon slips by?', subtitle: 'Great for research, summaries, file work, and multi-step delivery.', input: 'Give the agent a specific job to work on now...' },
    { title: `What should the agent finish this ${ctx.eveningWeekday} evening?`, subtitle: 'A good fit for closing out an analysis, a report, or a practical workflow.', input: 'Write what the agent should finish before tonight is over...' },
    { title: 'What do you want the agent to carry all the way through?', subtitle: 'It works best when you describe the goal, the output, and what matters most.', input: 'Describe the goal, the deliverable, and the boundaries...' },
    { title: 'What should the agent start working on right now?', subtitle: 'It can search, write, analyze, and deliver without you micromanaging every step.', input: 'Give the agent your task here...' },
  ]
}

function getEntries(mode: WelcomeMode, language: Language, context: CopyContext): WelcomeEntry[] {
  if (language === 'en') {
    return mode === 'agent' ? buildEnAgentEntries(context) : buildEnChatEntries(context)
  }
  return mode === 'agent' ? buildNoAgentEntries(context) : buildNoChatEntries(context)
}

function getMillisecondsUntilNextBucket(now: Date): number {
  const next = new Date(now)
  const nextHour = Math.floor(now.getHours() / 2) * 2 + 2
  next.setHours(nextHour, 0, 0, 0)
  return Math.max(1000, next.getTime() - now.getTime())
}

export function useRotatingWelcomeCopy(mode: WelcomeMode, language: Language) {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    let timeoutId: number | null = null

    const scheduleNext = () => {
      const current = new Date()
      setNow(current)
      timeoutId = window.setTimeout(scheduleNext, getMillisecondsUntilNextBucket(current) + 25)
    }

    timeoutId = window.setTimeout(scheduleNext, getMillisecondsUntilNextBucket(now) + 25)

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [now])

  return useMemo(() => {
    const context = getWeekdayNames(language, now)
    const bucket = Math.floor(now.getHours() / 2) % 12
    const entry = getEntries(mode, language, context)[bucket]
    return {
      ...entry,
      rotationKey: `${mode}-${language}-${now.getDay()}-${bucket}`,
      bucket,
    }
  }, [language, mode, now])
}
